import React, { FunctionComponent, useEffect, useState } from 'react'
import { useT, T } from '@transifex/react'
import { Button, Card, Icon, Text } from '@lightspeed/design-system-react'
import { CustomerBadge } from '@vend/react-utilities'
import {
  useCustomerManager,
  useRetailerSettingsManager,
} from '../../../ng-compat/hooks'
import { useAppSelector } from '../../../state/hooks'
import {
  selectSaleCustomer,
  selectOutstandingBalance,
  selectCurrentSale,
} from '../../../state/slices/currentSale/currentSaleSlice'
import {
  isAccountBalancePaymentAdded,
  isAccountBalancePaymentPaid,
  isBlindReturn,
  isReturn,
  isValidCustomer,
} from '../../../functions/sale'
import { getFeatureFromCache } from '../../../util/feature-cache'
import { FEATURE_ENABLE_CUSTOMER_LOYALTY_THRESHOLD } from '../../../constants/wr-features'
import { PartialCustomerGroup } from '../../react/modals/common/types'
import { CustomerSearch } from '../../customer/customer-search/CustomerSearch'
import { RemoveCustomerButton } from '../sale-customer-search/RemoveCustomerButton'
import { LockedCustomerIcon } from '../sale-customer-search/LockedCustomerIcon'
import { SaleStatus } from '../../../models/sale.constants'
import { AccountBalanceSetupModal } from '../account-balance-setup-modal/AccountBalanceSetupModal'
import { UpdateSaleCustomerModal } from '../../../modals/update-customer-modal'
import { CustomerBalancesSection } from './CustomerBalancesSection'
import { AddCustomerPopover } from './AddCustomerPopover'

interface Props {
  canAddAccountBalancePayment?: boolean
  onSelect: (s: any) => void
}

export const CustomerSection: FunctionComponent<Props> = ({
  canAddAccountBalancePayment,
  onSelect,
}) => {
  const t = useT()
  const saleCustomer = useAppSelector(selectSaleCustomer)
  const validCustomer = isValidCustomer(saleCustomer)
  const outstandingBalance = useAppSelector(selectOutstandingBalance)
  const retailerSettingsManager = useRetailerSettingsManager()
  const customerManager = useCustomerManager()
  const getCustomerGroup = customerManager.getCustomerGroup
  const sale = useAppSelector(selectCurrentSale)

  const [showAccountBalanceModal, setShowAccountBalanceModal] = useState(false)
  const [customerSearchQuery, setCustomerSearchQuery] = useState('')
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false)

  const [customerGroup, setCustomerGroup] = useState<
    PartialCustomerGroup | undefined
  >()
  const shouldShowCustomerGroup = getFeatureFromCache('customer_groups')
  const loyaltyThresholdFeatureEnabled = getFeatureFromCache(
    FEATURE_ENABLE_CUSTOMER_LOYALTY_THRESHOLD
  )

  useEffect(() => {
    if (shouldShowCustomerGroup && saleCustomer) {
      getCustomerGroup(saleCustomer)
        .then(group => {
          setCustomerGroup(group || undefined)
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.warn('Failed to get customer group', error)
        })
    }
  }, [saleCustomer, shouldShowCustomerGroup, getCustomerGroup])

  const getAddCustomerPlaceholder = () => {
    let placeholder = t('Add a customer to this sale', {
      _context: 'Payment panel customer search placeholder',
    })

    if (
      outstandingBalance > 0 ||
      retailerSettingsManager.isStoreCreditEnabled()
    ) {
      placeholder = t('Add a customer to pay with the following options:', {
        _context: 'Payment panel customer search placeholder',
      })
    }
    return placeholder
  }

  const showAccountBalancePaymentBanner = () => {
    return Boolean(
      canAddAccountBalancePayment &&
        sale.status !== SaleStatus.ACCOUNT_SALE &&
        sale.status !== SaleStatus.LAYBY_SALE &&
        !isReturn(sale) &&
        !isBlindReturn(sale) &&
        sale.customerBalances?.account &&
        sale.customerBalances.account < 0
    )
  }

  const hasAccountBalancePayment = () => {
    return isAccountBalancePaymentAdded(sale.payAccountBalance)
  }

  const hasPaidAccountBalancePayment = () => {
    return isAccountBalancePaymentPaid(sale.payAccountBalance)
  }

  const getAccountBalancePaymentBannerText = () => {
    if (hasAccountBalancePayment()) {
      if (hasPaidAccountBalancePayment()) {
        return (
          <T
            _str="An account balance payment has been added and paid for."
            _context="Customer account balance banner text"
          />
        )
      }
      return (
        <T
          _str="An account balance payment has been added."
          _context="Customer account balance banner text"
        />
      )
    }
    return (
      <T
        _str="This customer has an unpaid account balance."
        _context="Customer account balance banner text"
      />
    )
  }

  const getAccountBalanceBannerLinkText = () => {
    return hasAccountBalancePayment() ? (
      <T
        _str="Edit or remove"
        _context="Customer account balance banner modal link"
      />
    ) : (
      <T
        _str="Add a payment"
        _context="Customer account balance banner modal link"
      />
    )
  }
  const openAccountBalanceModal = () => {
    setShowAccountBalanceModal(true)
  }
  const closeAccountBalanceModal = () => setShowAccountBalanceModal(false)

  const openAddCustomerModal = () => {
    setShowAddCustomerModal(true)
  }
  const closeAddCustomerModal = () => setShowAddCustomerModal(false)

  return (
    <>
      <div
        id="wr-payment-add-customer"
        className={`vd-mt5 payment-add-customer${
          loyaltyThresholdFeatureEnabled ? ' customer-card' : ''
        }`}
      >
        {!validCustomer && (
          <div className="vd-flex">
            <AddCustomerPopover />
            <div style={{ width: '100%' }}>
              <CustomerSearch
                onAdd={openAddCustomerModal}
                onSelect={onSelect}
                onFetch={(query: string) => setCustomerSearchQuery(query)}
                placeholder={getAddCustomerPlaceholder()}
              ></CustomerSearch>
            </div>
          </div>
        )}
        {validCustomer && (
          <Card className="wr-customer-badge">
            {showAccountBalancePaymentBanner() && (
              <div className="vd-banner vd-banner--info wr-customer-badge-banner">
                <Text type="supplementary">
                  <Icon icon="fa-info-circle" className="vd-banner-icon"></Icon>
                  {getAccountBalancePaymentBannerText()}
                  &nbsp;
                  <Button
                    link
                    className="vd-link vd-link--secondary"
                    onClick={openAccountBalanceModal}
                    data-track="open-account-balance-modal"
                  >
                    {getAccountBalanceBannerLinkText()}
                  </Button>
                </Text>
              </div>
            )}
            <div
              className={`wr-customer-badge-content ${
                loyaltyThresholdFeatureEnabled
                  ? 'vd-pa2 vd-flex vd-flex-row vd-flex--justify-between'
                  : 'vd-pt4 vd-pb2 vd-pl4 vd-pr4'
              }`}
            >
              <CustomerBadge
                size="small"
                apiCustomer={saleCustomer}
                group={customerGroup}
              ></CustomerBadge>
              <RemoveCustomerButton />
              <LockedCustomerIcon />
            </div>
            <CustomerBalancesSection />
          </Card>
        )}
      </div>
      {showAccountBalanceModal && (
        <AccountBalanceSetupModal close={closeAccountBalanceModal} />
      )}
      {showAddCustomerModal && (
        <UpdateSaleCustomerModal
          onClose={closeAddCustomerModal}
          customerSearchQuery={customerSearchQuery}
        />
      )}
    </>
  )
}
